// Customer-side WebRTC helpers.

// STUN-only. Both endpoints are anycast and will resolve to a nearby
// PoP (Cloudflare Sydney / Google Sydney for AU users). For peers behind
// symmetric NAT, a TURN relay is needed — to add Cloudflare TURN, mint
// per-session credentials in the signaling worker via the Realtime TURN
// API and include them here as `{ urls: ['turn:turn.cloudflare.com:3478'],
// username, credential }`.
const DEFAULT_ICE = [
  { urls: ['stun:stun.cloudflare.com:3478'] },
  { urls: ['stun:stun.l.google.com:19302'] },
];

export function buildIceServers(extra = []) {
  return [...DEFAULT_ICE, ...extra];
}

export function createPeer({ iceServers, onTrack, onState, onIce, onChannel }) {
  const pc = new RTCPeerConnection({ iceServers });
  if (onTrack) pc.ontrack = onTrack;
  pc.onicecandidate = (e) => {
    if (e.candidate && onIce) onIce(e.candidate.toJSON());
  };
  if (onState) pc.oniceconnectionstatechange = () => onState(pc.iceConnectionState);
  if (onChannel) pc.ondatachannel = (e) => onChannel(e.channel);
  return pc;
}

export async function getCameraStream({ facingMode = 'environment' } = {}) {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Your browser does not support camera access.');
  }
  // Video only — Remote Eyes is a one-way camera share; no microphone.
  return navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: { ideal: facingMode },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      frameRate: { ideal: 30, max: 30 },
    },
  });
}

export async function replaceVideoTrack(pc, stream, facingMode) {
  const fresh = await navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: facingMode },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
  });
  const newTrack = fresh.getVideoTracks()[0];
  const old = stream.getVideoTracks()[0];
  if (old) {
    stream.removeTrack(old);
    old.stop();
  }
  stream.addTrack(newTrack);
  const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
  if (sender) await sender.replaceTrack(newTrack);
  return newTrack;
}

export function setTorch(track, enabled) {
  if (!track) return Promise.resolve(false);
  const caps = track.getCapabilities ? track.getCapabilities() : {};
  if (!caps.torch) return Promise.resolve(false);
  return track.applyConstraints({ advanced: [{ torch: enabled }] }).then(() => true);
}

export async function captureHiResStill(track) {
  // Prefer ImageCapture API for a proper still photo (uses native camera)
  if (typeof ImageCapture !== 'undefined' && track) {
    try {
      const ic = new ImageCapture(track);
      const blob = await ic.takePhoto();
      return blob;
    } catch (e) {
      // fall through to canvas grab
    }
  }
  return null;
}
